import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  sdh_rules, sdh_applyDecisions, sdh_smeTaxonomy, SME_ASSESSMENTS, GATE_TONE,
  v2Status, previousTitle, titleChanged,
  type EnrichedRule, type AssessmentDef, type V2Gate,
} from "../api/sdhAPI_v2";
import { measurementOf, NotMeasured, impactText } from "../dq/measurement";
import DecidePopoverV2 from "./DecidePopoverV2";
import RuleDetailV2 from "./RuleDetailV2";
import { useAuth } from "../hooks/useAuth";

/**
 * ReviewV2 — one rule, one row.
 *
 * What this page removes, and why:
 *  - There is no "Agent revisions" tab. A revised rule does not move; it stays here, in its field group,
 *    marked "✎ revised". A rule can never appear in two places, which is what made v1 confusing.
 *  - The card shows four things: title, what it enforces, impact, and one Decide button. Every
 *    classification badge (trust, risk, dimension, family, scope) is behind "Why this needs review", so
 *    the model's opinion never competes with her verdict.
 *  - Deciding saves immediately. There is no separate batch tray for a single decision.
 */
const GATES: { key: V2Gate | "ALL"; label: string }[] = [
  { key: "ALL", label: "All" },
  { key: "NOT_REVIEWED", label: "Not reviewed" },
  { key: "APPROVED", label: "Approved" },
  { key: "CONDITIONAL", label: "Conditional" },
  { key: "PENDING", label: "Pending" },
  { key: "REJECTED", label: "Rejected" },
];
const tone = (g: string) => (GATE_TONE as any)[g] || "#8a97a3";
const fieldOf = (r: EnrichedRule) =>
  String((r as any).owner_field || (r as any)._raw?.owner_field_id || "—");

export default function ReviewV2(
  { entityId, appNm, entityLabel }: { entityId: string; appNm: string; entityLabel?: string }
) {
  const { user } = useAuth();
  const [rules, setRules] = useState<EnrichedRule[]>([]);
  const [defs, setDefs] = useState<AssessmentDef[]>(SME_ASSESSMENTS);
  const [busy, setBusy] = useState(true);
  const [saving, setSaving] = useState(false);
  const [gate, setGate] = useState<V2Gate | "ALL">("NOT_REVIEWED");
  const [onlyRevised, setOnlyRevised] = useState(false);
  const [q, setQ] = useState("");
  const [decideFor, setDecideFor] = useState<EnrichedRule | null>(null);
  const [detailFor, setDetailFor] = useState<EnrichedRule | null>(null);
  const [why, setWhy] = useState<Record<string, boolean>>({});
  const [flash, setFlash] = useState<string | null>(null);

  const note = (m: string) => { setFlash(m); setTimeout(() => setFlash(null), 3000); };

  const load = useCallback(async () => {
    setBusy(true);
    try { const r = await sdh_rules(entityId, "All"); setRules(r.rules || []); }
    catch { setRules([]); } finally { setBusy(false); }
  }, [entityId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { sdh_smeTaxonomy().then(setDefs).catch(() => {}); }, []);

  // Record<string, number> explicitly: the object spread was inferring the literal shape
  // `{ REVISED: number }`, so indexing it by a V2Gate failed under strict mode.
  const counts = useMemo<Record<string, number>>(() => {
    const c: Record<string, number> = { ALL: rules.length, REVISED: 0 };
    rules.forEach((r) => {
      const st = v2Status(r);
      c[st.gate] = (c[st.gate] || 0) + 1;
      if (st.awaitingConfirmation) c.REVISED += 1;
    });
    return c;
  }, [rules]);

  const shown = useMemo(() => rules.filter((r) => {
    const s = v2Status(r);
    if (gate !== "ALL" && s.gate !== gate) return false;
    if (onlyRevised && !s.awaitingConfirmation) return false;
    if (q) {
      const hay = `${r.rule_name} ${fieldOf(r)} ${(r as any).business_logic || ""}`.toLowerCase();
      if (!hay.includes(q.toLowerCase())) return false;
    }
    return true;
  }), [rules, gate, onlyRevised, q]);

  const grouped = useMemo(() => {
    const m: Record<string, EnrichedRule[]> = {};
    shown.forEach((r) => { const k = fieldOf(r); (m[k] = m[k] || []).push(r); });
    return m;
  }, [shown]);

  const decide = async (rule: EnrichedRule, assessment: string, comment: string) => {
    const def = defs.find((d) => d.value === assessment) || SME_ASSESSMENTS.find((d) => d.value === assessment);
    setSaving(true);
    try {
      await sdh_applyDecisions(entityId, appNm, [{
        constraint_id: rule.rule_id, decision_type: "rule_review",
        choice: def?.choice || "reject_rule", assessment, decision_gate: def?.gate,
        comment, sme_id: user?.user_id || user?.email || "unknown",
        sme_name: user?.full_name || user?.email,
      }]);
      note(`Saved — ${def?.label || assessment}.`);
      setDecideFor(null);
      await load();
    } catch { note("Could not save the decision."); }
    finally { setSaving(false); }
  };

  return (
    <div className="v2">
      <style>{CSS}</style>

      <header className="v2-top">
        <div>
          <h2>Review rules{entityLabel ? ` — ${entityLabel}` : ""}</h2>
          <p>One row per rule, always the current version. Decide, and it saves.</p>
        </div>
      </header>

      <div className="v2-gates">
        {GATES.map((g) => (
          <button key={g.key} className={"seg" + (gate === g.key ? " on" : "")}
            style={{ ["--t" as any]: g.key === "ALL" ? "#42586e" : tone(g.key) }}
            onClick={() => setGate(g.key)}>
            <span className="n">{counts[g.key] ?? 0}</span><span className="l">{g.label}</span>
          </button>
        ))}
        {counts.REVISED > 0 && (
          <button className={"chip" + (onlyRevised ? " on" : "")}
            title="The agent changed these after you reviewed them — confirm the change still matches your decision"
            onClick={() => setOnlyRevised((v) => !v)}>
            ✎ Revised since your review <em>{counts.REVISED}</em>
          </button>
        )}
      </div>

      <div className="v2-search">
        <input placeholder="Search rules, fields…" value={q} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setQ(e.target.value)} />
        <span className="cnt">{shown.length} of {rules.length}</span>
      </div>

      {busy ? <div className="v2-empty">Loading…</div>
        : shown.length === 0 ? <div className="v2-empty">No rules match this view.</div>
        : Object.keys(grouped).sort().map((f) => (
          <section key={f} className="v2-grp">
            <div className="v2-grph">{f.split("::").pop()} <span className="fid">{f.replace("::", ".")}</span>
              <span className="n">{grouped[f].length} rule(s)</span></div>

            {grouped[f].map((r) => {
              const s = v2Status(r);
              const m = measurementOf(r);
              const prev = previousTitle(r);
              const open = !!why[r.rule_id];
              return (
                <article className="v2-card" key={r.rule_id} style={{ ["--t" as any]: s.tone }}>
                  <div className="v2-title">
                    <b>{r.rule_name || r.rule_id}</b>
                    <span className="st" style={{ background: s.tone }}>{s.label}</span>
                    {s.awaitingConfirmation &&
                      <span className="rev" title="Changed after your review — confirm it still stands">✎ revised</span>}
                  </div>
                  {s.awaitingConfirmation && prev && titleChanged(r) &&
                    <div className="was">was “{prev}”</div>}

                  <p className="v2-what">{(r as any).business_logic || (r as any).description || ""}</p>

                  <div className="v2-impact">
                    {m.ok
                      ? <span className={m.violations ? "bad" : "good"}>
                          {m.violations ? `Flags ${impactText(m)}` : `Clean — ${(m.applicable ?? 0).toLocaleString()} records pass`}
                        </span>
                      : <NotMeasured m={m} compact />}
                  </div>

                  {s.comment && (
                    <div className="v2-yours">
                      <b>{s.assessmentLabel || s.label}</b>
                      <span className="w">{s.by}{s.at ? ` · ${s.at}` : ""}{s.reviewedVersion ? ` · v${s.reviewedVersion}` : ""}</span>
                      <div className="c">“{s.comment}”</div>
                    </div>
                  )}

                  <div className="v2-acts">
                    <button className="btn go" onClick={() => setDecideFor(r)}>
                      {s.gate === "NOT_REVIEWED" ? "Decide" : s.awaitingConfirmation ? "Confirm or change" : "Change decision"}
                    </button>
                    <button className="btn ghost" onClick={() => setDetailFor(r)}>Open rule</button>
                    <button className="lnk" onClick={() => setWhy((w) => ({ ...w, [r.rule_id]: !w[r.rule_id] }))}>
                      {open ? "hide" : "why this needs review"}
                    </button>
                  </div>

                  {open && (() => {
                    const rc: any = (r as any).review_classification || {};
                    const t: any = (r as any).tags || {};
                    return (
                      <div className="v2-why">
                        {rc.trust_label && <span>Trust <b>{rc.trust_label}</b></span>}
                        {rc.risk_label && <span>Risk <b>{rc.risk_label}</b></span>}
                        {t.dimension && <span>Dimension <b>{t.dimension}</b></span>}
                        {(r as any).family && <span>Type <b>{(r as any).family}</b></span>}
                        {t.suspect_overbroad && <span className="warn">⚠ flags an implausible share of rows — likely a rule defect</span>}
                        {rc.explain?.reason && <div className="ex">{rc.explain.reason}</div>}
                      </div>
                    );
                  })()}
                </article>
              );
            })}
          </section>
        ))}

      {decideFor && (
        <DecidePopoverV2
          ruleName={decideFor.rule_name || decideFor.rule_id}
          current={{ assessment: (decideFor as any).sme_decision?.assessment,
                     comment: (decideFor as any).sme_decision?.comment }}
          defs={defs} busy={saving}
          onCancel={() => setDecideFor(null)}
          onConfirm={(a, c) => decide(decideFor, a, c)} />
      )}
      {detailFor && (
        <RuleDetailV2 entityId={entityId} appNm={appNm} rule={detailFor} defs={defs}
          onClose={() => setDetailFor(null)}
          onChanged={(m) => { note(m); setDetailFor(null); load(); }} />
      )}
      {flash && <div className="v2-flash">{flash}</div>}
    </div>
  );
}

const CSS = `
.v2{--line:#e3e8ee;--muted:#6b7c8d;font-family:system-ui,-apple-system,sans-serif;color:#0E2436;padding:4px 2px 40px}
.v2-top h2{font-size:19px;margin:0 0 2px;font-family:'Space Grotesk',system-ui,sans-serif}
.v2-top p{margin:0 0 14px;font-size:13px;color:var(--muted)}
.v2-gates{display:flex;gap:8px;flex-wrap:wrap;align-items:center;margin-bottom:12px}
.v2-gates .seg{all:unset;cursor:pointer;min-width:96px;padding:9px 13px;border:1px solid var(--line);
 border-radius:11px;background:#fff}
.v2-gates .seg:hover{background:#f8fafb}
.v2-gates .seg.on{border-color:var(--t);box-shadow:0 0 0 2px color-mix(in srgb,var(--t) 18%,#fff)}
.v2-gates .seg .n{display:block;font-family:ui-monospace,monospace;font-size:19px;font-weight:600;color:var(--t)}
.v2-gates .seg .l{font-size:11px;color:var(--muted);text-transform:uppercase;letter-spacing:.05em}
.v2-gates .chip{all:unset;cursor:pointer;margin-left:auto;display:inline-flex;gap:7px;align-items:center;
 font-size:12.5px;padding:7px 13px;border-radius:20px;background:#eef2f8;color:#43607e}
.v2-gates .chip.on{background:#43607e;color:#fff}
.v2-gates .chip em{font-style:normal;font-family:ui-monospace,monospace;font-size:11px}
.v2-search{display:flex;align-items:center;gap:10px;margin-bottom:14px}
.v2-search input{flex:1;font:inherit;font-size:13px;padding:9px 13px;border:1px solid var(--line);border-radius:10px}
.v2-search input:focus{outline:none;border-color:#0e7c7b;box-shadow:0 0 0 2px #d9ecea}
.v2-search .cnt{font-size:12px;color:var(--muted);font-family:ui-monospace,monospace}
.v2-empty{padding:40px;text-align:center;color:var(--muted)}
.v2-grp{margin-bottom:18px}
.v2-grph{display:flex;align-items:baseline;gap:9px;font-size:13.5px;font-weight:700;margin-bottom:7px}
.v2-grph .fid{font-family:ui-monospace,monospace;font-size:11px;color:var(--muted);font-weight:400}
.v2-grph .n{margin-left:auto;font-size:11.5px;color:var(--muted);font-weight:400}
.v2-card{border:1px solid var(--line);border-left:3px solid var(--t);border-radius:11px;padding:12px 14px;
 margin-bottom:8px;background:#fff}
.v2-card:hover{box-shadow:0 3px 12px rgba(16,36,54,.06)}
.v2-title{display:flex;align-items:center;gap:9px;flex-wrap:wrap}
.v2-title b{font-size:14px}
.v2-title .st{font-size:10px;font-weight:700;letter-spacing:.05em;color:#fff;border-radius:20px;padding:2px 9px}
.v2-title .rev{font-size:10.5px;font-weight:600;background:#eef2f8;color:#43607e;border-radius:20px;padding:2px 9px}
.was{font-size:11.5px;color:var(--muted);margin-top:3px;font-style:italic}
.v2-what{margin:7px 0 9px;font-size:13px;color:#33404e;line-height:1.55}
.v2-impact{font-size:12.5px;margin-bottom:9px}
.v2-impact .bad{color:#b23a30;font-weight:600}
.v2-impact .good{color:#2f7a4f;font-weight:600}
.v2-yours{border-left:3px solid var(--t);background:#f8fafb;border-radius:8px;padding:7px 11px;margin-bottom:9px}
.v2-yours b{font-size:11.5px;color:var(--t)}
.v2-yours .w{font-size:11px;color:var(--muted);margin-left:8px;font-family:ui-monospace,monospace}
.v2-yours .c{font-size:12.5px;color:#33404e;font-style:italic;margin-top:3px;line-height:1.5}
.v2-acts{display:flex;align-items:center;gap:9px}
.btn{all:unset;cursor:pointer;padding:7px 15px;border-radius:9px;font-size:13px;font-weight:600}
.btn.go{background:#0e7c7b;color:#fff}.btn.ghost{background:#eef1f5;color:#42586e}
.lnk{all:unset;cursor:pointer;font-size:12px;color:#0e7c7b;margin-left:auto}
.lnk:hover{text-decoration:underline}
.v2-why{margin-top:9px;padding-top:9px;border-top:1px solid var(--line);display:flex;gap:14px;flex-wrap:wrap;font-size:12px;color:var(--muted)}
.v2-why b{color:#33404e}
.v2-why .warn{color:#946011}
.v2-why .ex{flex-basis:100%;color:#4a5763;line-height:1.5}
.v2-flash{position:fixed;left:50%;bottom:26px;transform:translateX(-50%);z-index:95;background:#0e2436;color:#fff;
 padding:10px 18px;border-radius:10px;font-size:13px;box-shadow:0 10px 30px rgba(0,0,0,.25)}
`;
